import type {
  BillboardDetailData,
  BillboardMedia,
} from '@/features/billboards/api/billboardApi';

export function isVideoMedia(url?: string, mediaType?: string): boolean {
  if (mediaType?.toLowerCase().includes('video')) return true;
  return Boolean(url?.toLowerCase().endsWith('.mp4'));
}

export function formatFieldLabel(value?: string): string {
  if (!value) return '—';
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Display screen names as Title Case (first letter capital, rest lowercase). */
export function formatScreenName(value?: string | null, fallback = 'Unknown screen') {
  const trimmed = value?.trim();
  if (!trimmed) return fallback;
  return formatFieldLabel(trimmed);
}

export function resolveDisplayCurrency(): 'AED' | 'INR' {
  if (typeof window === 'undefined') return 'INR';
  return window.location.hostname.includes('.ae') ? 'AED' : 'INR';
}

export function resolveBillboardMedia(
  billboard: BillboardDetailData,
): BillboardMedia[] {
  if (billboard.mediaIds?.length) return billboard.mediaIds;
  if (billboard.thumbnail?.url) {
    return [{ url: billboard.thumbnail.url, mediaType: 'image' }];
  }
  return [];
}

export function buildFullAddress(billboard: BillboardDetailData): string {
  if (billboard.address?.fullAddress) return billboard.address.fullAddress;

  const parts = [
    billboard.address?.addressLine1 || billboard.address?.secondaryAddress,
    billboard.address?.area || billboard.address?.locality,
    billboard.address?.building,
    billboard.address?.city,
    billboard.address?.state,
    billboard.address?.pincode || billboard.pincode,
    billboard.address?.country || 'India',
  ].filter(Boolean);

  return parts.join(', ') || 'No address provided';
}

export function resolveCampaignCtaLabel(
  hasToken: boolean,
  canCreate: boolean,
  compact = false,
  isClient = false,
): string {
  if (isClient) return compact ? 'Run ads yourself' : 'Register as Personal Advertiser';
  if (!hasToken) return 'Create campaign';
  if (!canCreate) return compact ? 'Select date' : 'Select a date to continue';
  return 'Create campaign';
}

export function getGoogleMapsApiKey(): string | null {
  const key = import.meta.env.VITE_GOOGLE_LOCATION_KEY?.trim();
  return key || null;
}
